import type { Player } from '@infiltre/shared';
import { motion } from 'motion/react';
import { useResolvedLayout } from '@/shared/lib/layout';
import { mugshotForPlayer } from '@/shared/lib/mugshot';
import { MugshotPortrait, PaperSheet, PhaseHeader } from '@/shared/ui';

export type PlayerReadyRosterProps = {
  /** Roster to render — typically alive players for vote-wait, all for reveal-wait. */
  players: readonly Player[];
  /** Player ids that have reported "ready" (acknowledged / voted). */
  readyIds: readonly string[];
  /** Pinned phase header copy. */
  eyebrow: string;
  title: string;
  /** Italic red sub-line. Interpolate ready/total here. */
  descriptor: string;
  /** Centered ring label under the ready/total count, e.g. "ready" / "voted". */
  centerLabel: string;
  /** Highlights the viewer's own polaroid with a thin accent ring. */
  selfId?: string;
  /**
   * Players to render dimmed and *not* counted in the ready/pending split —
   * e.g. eliminated players watching the vote phase.
   */
  inactiveIds?: readonly string[];
};

const TILTS = [-4, 3, -2, 4, -3, 5, -5, 2];

/**
 * Speaking-ring style roster used while the engine is waiting on a
 * unanimous-ish action (reveal-ack, vote-cast). Each polaroid renders
 * one of three states: ready (green stamp), pending (dimmed + dots),
 * inactive (washed out, ignored by the count).
 */
export const PlayerReadyRoster = ({
  players,
  readyIds,
  eyebrow,
  title,
  descriptor,
  centerLabel,
  selfId,
  inactiveIds = [],
}: PlayerReadyRosterProps) => {
  const layout = useResolvedLayout();
  const isDesktop = layout === 'desktop';

  const ringSize = isDesktop ? 460 : 320;
  const ringR = isDesktop ? 175 : 120;
  const cardW = isDesktop ? 96 : 76;
  const cardH = isDesktop ? 116 : 92;
  const cx = ringSize / 2;
  const cy = ringSize / 2;

  return (
    <main className="mx-auto flex h-full w-full max-w-5xl flex-col">
      <PhaseHeader eyebrow={eyebrow} title={title} descriptor={descriptor} />

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 pb-2">
        <div className="relative flex-shrink-0" style={{ width: ringSize, height: ringSize }}>
          {players.map((p, i) => {
            const angle = (i / players.length) * Math.PI * 2 - Math.PI / 2;
            const x = Math.cos(angle) * ringR + cx - cardW / 2;
            const y = Math.sin(angle) * ringR + cy - cardH / 2;
            const tilt = TILTS[i % TILTS.length]!;
            const initial = p.name.charAt(0).toUpperCase();
            const inactive = inactiveIds.includes(p.id);
            const ready = !inactive && readyIds.includes(p.id);
            const isSelf = selfId === p.id;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, scale: 0.8, rotate: 0 }}
                animate={{ opacity: inactive ? 0.4 : 1, scale: 1, rotate: tilt }}
                transition={{ delay: 0.05 * i, duration: 0.32 }}
                className="absolute"
                style={{ left: x, top: y, width: cardW }}
              >
                <PaperSheet
                  tone="cream"
                  seed={p.id}
                  className="relative px-1 pt-1 pb-1.5"
                  style={{
                    boxShadow: 'var(--shadow-paper-soft)',
                    outline: isSelf ? '2px solid var(--color-und)' : undefined,
                    outlineOffset: isSelf ? 2 : undefined,
                    filter: ready || inactive ? undefined : 'saturate(0.7)',
                  }}
                >
                  <ReadyBadge state={inactive ? 'inactive' : ready ? 'ready' : 'pending'} />
                  <div
                    className="relative flex w-full items-center justify-center overflow-hidden border border-ink/15"
                    style={{ height: cardH - 30 }}
                  >
                    <MugshotPortrait config={mugshotForPlayer(p)} />
                    <span className="absolute right-1 bottom-0.5 font-mono text-[8px] text-ink/55">
                      {initial}
                    </span>
                  </div>
                </PaperSheet>
                <div className="mt-0.5 truncate text-center font-hand text-[14px] text-cream leading-tight">
                  {p.name}
                </div>
              </motion.div>
            );
          })}

          <RingCenter
            ready={readyIds.filter((id) => !inactiveIds.includes(id)).length}
            total={players.length - inactiveIds.length}
            label={centerLabel}
          />
        </div>
      </div>
    </main>
  );
};

type BadgeState = 'ready' | 'pending' | 'inactive';

const ReadyBadge = ({ state }: { state: BadgeState }) => {
  if (state === 'ready') {
    return (
      <span
        aria-hidden
        className="absolute -top-1.5 -right-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 border-ink bg-cream font-display font-extrabold text-[12px]"
        style={{
          boxShadow: 'var(--shadow-stamp-xs)',
          color: 'var(--color-stamp-green, #166534)',
          transform: 'rotate(-8deg)',
        }}
      >
        ✓
      </span>
    );
  }
  if (state === 'inactive') {
    return null;
  }
  return (
    <span
      aria-hidden
      className="absolute -top-1.5 -right-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 border-ink/40 bg-cream/80"
      style={{ boxShadow: 'var(--shadow-stamp-xs)' }}
    >
      <span className="flex gap-[2px]">
        <Dot delay={0} />
        <Dot delay={0.18} />
        <Dot delay={0.36} />
      </span>
    </span>
  );
};

const Dot = ({ delay }: { delay: number }) => (
  <motion.span
    className="block h-1 w-1 rounded-full bg-ink/70"
    animate={{ opacity: [0.3, 1, 0.3] }}
    transition={{ duration: 0.9, delay, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
  />
);

const RingCenter = ({ ready, total, label }: { ready: number; total: number; label: string }) => (
  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 text-center">
    <div className="type text-[10px] text-cream/55 uppercase tracking-[0.22em]">
      {ready} / {total}
    </div>
    <div className="font-display font-extrabold text-[28px] text-cream uppercase italic leading-none sm:text-[36px]">
      {label}
    </div>
  </div>
);
